import { useEffect } from 'react';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  joinName,
  splitName,
  toDateInputValue,
} from '@/modules/service-requests/shared/application-utils';
import { readBankInfo } from '@/modules/service-requests/shared/bank-info';
import {
  fetchEmployeeSnapshot,
  useCurrentUserId,
  useEmployeeSnapshot,
} from '@/modules/service-requests/shared/employee';
import { translatedResolver } from '@/modules/service-requests/shared/form-resolver';
import { LookupItem, useServiceDeskDepartments } from '@/modules/service-requests/shared/lookups';
import { ApplyFormPage } from '@/modules/service-requests/shared/components/apply-form-page';
import {
  FieldGrid,
  PhoneField,
  SelectField,
  TextField,
} from '@/modules/service-requests/shared/components/form-fields';
import {
  useMetlifeApplication,
  useSubmitMetlife,
  useTakenMetlifeTypes,
} from '../../hooks/use-metlife';
import {
  asksMaternityBenefit,
  dependantDesignation,
  METLIFE_APPLICATION_TYPES,
  genderLabelKey,
  METLIFE_GENDERS,
  NO_METLIFE_ID,
} from '../../utils/metlife-rules';
import { MetlifeApplyValues, metlifeApplySchema } from '../../schemas/metlife-apply-form-schema';
import { MetlifeGender } from '../../types/metlife.types';
import { appPath, SERVICE_REQUEST_TYPES } from '@/constant/app-paths';

const EMPTY_VALUES: MetlifeApplyValues = {
  typeOfApplication: undefined as unknown as MetlifeApplyValues['typeOfApplication'],
  metlifeIdNumber: NO_METLIFE_ID,
  firstName: '',
  lastName: '',
  email: '',
  policyApplicantName: '',
  designation: '',
  dateOfBirth: '',
  mobileNumber: '',
  accountNumber: '',
  branchName: '',
  routingNumber: '',
  departmentId: '',
  gender: undefined as unknown as MetlifeGender,
  wantsMaternityBenefit: '',
};

/** The employee profile stores gender as free text; the form only offers the two legacy options. */
const toGender = (sex: string): MetlifeGender | undefined => {
  const upper = sex.trim().toUpperCase();
  return upper === 'MALE' || upper === 'FEMALE' ? upper : undefined;
};

export const MetlifeApplyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('id') ?? undefined;

  const currentUserId = useCurrentUserId();
  const { snapshot, isLoading: isSnapshotLoading } = useEmployeeSnapshot();
  const { data: existing, isLoading: isExistingLoading } = useMetlifeApplication(applicationId);
  const { data: departments = [] as LookupItem[], isLoading: isDepartmentsLoading } =
    useServiceDeskDepartments();

  // Whose policy this is: the applicant of the application a reviewer edits, else the signed-in
  // employee. Their profile fills the employee's own fields and their applications hold the slots.
  const ownerId = existing?.UserId || (applicationId ? '' : currentUserId);
  const isReviewerEdit = Boolean(existing && existing.UserId !== currentUserId);
  const { data: applicantSnapshot, isLoading: isApplicantLoading } = useGlobalQuery({
    queryKey: ['hr-service-desk-employee', existing?.UserId],
    queryFn: () => fetchEmployeeSnapshot(existing?.UserId as string),
    enabled: isReviewerEdit,
    staleTime: 5 * 60 * 1000,
  });
  const ownerSnapshot = isReviewerEdit ? (applicantSnapshot ?? null) : snapshot;
  const { taken, isLoading: isTakenLoading } = useTakenMetlifeTypes(
    ownerId,
    existing?.TypeOfApplication
  );
  const { mutateAsync: submit, isPending } = useSubmitMetlife();

  const form = useForm<MetlifeApplyValues>({
    resolver: translatedResolver(metlifeApplySchema, t),
    defaultValues: EMPTY_VALUES,
  });
  const type = form.watch('typeOfApplication');
  const gender = form.watch('gender');
  const isEmployeeType = type === 'Employee';
  const showMaternity = asksMaternityBenefit(type ?? '', gender ?? '');

  useEffect(() => {
    if (applicationId) {
      if (!existing) return;
      const { firstName, lastName } = splitName(existing.Name);
      const bank = readBankInfo(existing.BankInfoJson);
      form.reset({
        typeOfApplication: existing.TypeOfApplication,
        metlifeIdNumber: existing.MetLifeIdNumber || NO_METLIFE_ID,
        firstName,
        lastName,
        email: existing.Email ?? '',
        policyApplicantName: existing.PolicyApplicantName ?? '',
        designation: existing.Designation ?? '',
        dateOfBirth: toDateInputValue(existing.DateOfBirth),
        mobileNumber: existing.MobileNumber ?? '',
        accountNumber: bank.AccountNumber ?? '',
        branchName: bank.BranchName ?? '',
        routingNumber: bank.RoutingNumber ?? '',
        departmentId: existing.RequestDepartmentId ?? '',
        gender: existing.Gender,
        wantsMaternityBenefit:
          existing.WantsMaternityBenefit === true
            ? 'yes'
            : existing.WantsMaternityBenefit === false
              ? 'no'
              : '',
      });
      return;
    }
    if (!snapshot) return;
    const { firstName, lastName } = splitName(snapshot.displayName);
    form.reset({
      ...EMPTY_VALUES,
      firstName,
      lastName,
      email: snapshot.email,
      designation: snapshot.designation,
      mobileNumber: snapshot.phoneNumber,
      departmentId: snapshot.departmentId,
      gender: toGender(snapshot.sex) ?? EMPTY_VALUES.gender,
    });
  }, [applicationId, existing, snapshot, form]);

  // Choosing a type re-derives the fields that depend on whose policy this is: the employee's own
  // policy takes their designation and gender; a dependant's gender is asked afresh (legacy form).
  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name === 'typeOfApplication') {
        if (values.typeOfApplication === 'Employee') {
          form.setValue('metlifeIdNumber', NO_METLIFE_ID);
          form.setValue('designation', ownerSnapshot?.designation ?? '');
          form.setValue('gender', toGender(ownerSnapshot?.sex ?? '') ?? EMPTY_VALUES.gender);
        } else {
          form.setValue('metlifeIdNumber', '');
          form.setValue(
            'designation',
            dependantDesignation(joinName(values.firstName ?? '', values.lastName ?? ''))
          );
          form.setValue('gender', EMPTY_VALUES.gender);
        }
      }
      // A dependant's "designation" follows the name typed into the form.
      if (
        (name === 'firstName' || name === 'lastName') &&
        values.typeOfApplication !== 'Employee'
      ) {
        if (values.typeOfApplication) {
          form.setValue(
            'designation',
            dependantDesignation(joinName(values.firstName ?? '', values.lastName ?? ''))
          );
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form, ownerSnapshot]);

  // The maternity answer only exists for a female employee or spouse.
  useEffect(() => {
    if (!showMaternity) form.setValue('wantsMaternityBenefit', '');
  }, [showMaternity, form]);

  const goBack = () => navigate(appPath.serviceRequests(SERVICE_REQUEST_TYPES.INSURANCE));

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await submit({ existing: existing ?? undefined, values });
      goBack();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  });

  const departmentOptions = departments.map((department: LookupItem) => ({
    value: department.ItemId,
    label: department.Title,
  }));
  // A legacy record's department was stored as free text (BusinessUnitName) with no id link, so an
  // edited application may load with an empty departmentId even though it has a prior value on file.
  const legacyDepartmentName =
    existing && !existing.RequestDepartmentId
      ? existing.BusinessUnitName || existing.RequestDepartmentName
      : '';

  return (
    <ApplyFormPage
      form={form}
      titleKey="METLIFE_APPLICATION_FORM"
      isSubmitting={isPending}
      isEdit={Boolean(applicationId)}
      isLoading={
        applicationId
          ? isExistingLoading || (isReviewerEdit && isApplicantLoading)
          : isSnapshotLoading
      }
      // A new application needs the applicant's profile (prefill and department); an edit does not.
      submitDisabled={!applicationId && !snapshot}
      onCancel={goBack}
      onSubmit={handleSubmit}
    >
      <FieldGrid>
        <TextField
          control={form.control}
          name="metlifeIdNumber"
          // Fixed to N/A (locked) for the employee's own policy; only a dependant types an id.
          required={Boolean(type) && !isEmployeeType}
          label={t('METLIFE_ID_NUMBER')}
          placeholder={t('TYPE_METLIFE_ID')}
          description={isEmployeeType ? undefined : t('METLIFE_ID_HINT')}
          disabled={!type || isEmployeeType}
        />
        <SelectField
          control={form.control}
          name="typeOfApplication"
          required
          label={t('TYPE_OF_APPLICATION')}
          placeholder={t('SELECT_APPLICATION_TYPE')}
          options={
            isTakenLoading
              ? []
              : METLIFE_APPLICATION_TYPES.map((option) => ({
                  value: option.value,
                  label: option.label,
                  disabled: taken.has(option.value),
                }))
          }
          isLoading={isTakenLoading}
        />
        <TextField control={form.control} name="firstName" required label={t('FIRST_NAME')} />
        <TextField control={form.control} name="lastName" required label={t('LAST_NAME')} />
        <TextField control={form.control} name="email" required type="email" label={t('EMAIL')} />
        <TextField
          control={form.control}
          name="policyApplicantName"
          required
          label={t('POLICY_APPLICANT_NAME')}
          placeholder={t('TYPE_POLICY_APPLICANT_NAME')}
        />
        <TextField
          control={form.control}
          name="designation"
          required
          label={t('DESIGNATION')}
          disabled={Boolean(type) && !isEmployeeType}
        />
        <TextField
          control={form.control}
          name="dateOfBirth"
          required
          type="date"
          label={t('POLICY_APPLICANT_DATE_OF_BIRTH')}
        />
        <PhoneField
          control={form.control}
          name="mobileNumber"
          required
          label={t('MOBILE_NUMBER')}
        />
        <SelectField
          control={form.control}
          name="gender"
          required
          label={t('POLICY_APPLICANT_GENDER')}
          options={[
            ...METLIFE_GENDERS.map((option) => ({
              value: option.value,
              label: t(option.labelKey),
            })),
            // An imported record can hold a gender the form no longer offers; keep it selectable.
            ...(existing?.Gender && !METLIFE_GENDERS.some((g) => g.value === existing.Gender)
              ? [{ value: existing.Gender, label: t(genderLabelKey(existing.Gender)) }]
              : []),
          ]}
        />
        <SelectField
          control={form.control}
          name="departmentId"
          required
          label={t('DEPARTMENT')}
          options={departmentOptions}
          isLoading={isDepartmentsLoading}
          description={
            legacyDepartmentName
              ? `${t('PREVIOUSLY_RECORDED_AS')}: ${legacyDepartmentName}`
              : undefined
          }
        />
        <TextField
          control={form.control}
          name="accountNumber"
          required
          label={t('PRIME_BANK_ACCOUNT')}
          placeholder={t('TYPE_BANK_ACCOUNT')}
        />
        <TextField
          control={form.control}
          name="branchName"
          required
          label={t('BRANCH_NAME')}
          placeholder={t('TYPE_BRANCH_NAME')}
        />
        <TextField
          control={form.control}
          name="routingNumber"
          required
          label={t('ROUTING_NO')}
          placeholder={t('TYPE_ROUTING_NO')}
        />
      </FieldGrid>

      {showMaternity && (
        <SelectField
          control={form.control}
          name="wantsMaternityBenefit"
          required
          label={t('WANTS_MATERNITY_BENEFIT')}
          options={[
            { value: 'yes', label: t('YES') },
            { value: 'no', label: t('NO') },
          ]}
        />
      )}
    </ApplyFormPage>
  );
};
